import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchApi } from "@/lib/api/index.ts";

export enum GAME_TYPE {
  TAP_COUNTER = "tap_counter",
  SEQUENCE_REPEATER = "sequence_repeater",
  ASCENT = "ascent",
}

export enum GAME_STATUS {
  STARTED = "started",
  COMPLETED = "completed",
  CANCELLED = "cancelled",
}

export interface GameSession {
  id: string;
  player: {
    id: string;
    name: string;
    profile_picture_url?: string;
  };
  game_type: GAME_TYPE;
  status: GAME_STATUS;
  final_score?: number;
  duration_seconds?: number;
  completed_at?: string;
  created_at: string;
  updated_at: string;
}

interface StartGameParams {
  game_type: GAME_TYPE;
  mentorship_id: string;
  sendbird_channel_url: string;
}

interface EndGameParams {
  game_session_id: string;
  final_score: number;
}

interface CreateMentorGameParams {
  mentorship_id: string;
  game_type: GAME_TYPE;
  final_score: number;
  sendbird_channel_url: string;
}

export const useGetActiveGame = () => {
  return useQuery<GameSession | null>({
    queryKey: ["active-game"],
    queryFn: async () => {
      const response = await fetchApi<{ active_game: GameSession | null }>({
        url: `/v2/games/active`,
        method: "GET",
      });

      return response.data.active_game;
    },
  });
};

export const useGetGameSessions = (mentorshipId: string) => {
  return useQuery<GameSession[]>({
    queryKey: ["game-sessions", mentorshipId],
    queryFn: async () => {
      const response = await fetchApi<{ game_sessions: GameSession[] }>({
        url: `/v2/games/sessions/${mentorshipId}`,
        method: "GET",
      });

      if (!response.data?.game_sessions)
        throw new Error("No game sessions found");

      return response.data.game_sessions;
    },
    enabled: !!mentorshipId,
  });
};

export const useStartGame = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["start-game"],
    mutationFn: async (data: StartGameParams) => {
      const response = await fetchApi<{ game_session: GameSession }>({
        url: `/v2/games/start`,
        method: "POST",
        data,
      });

      return response.data.game_session;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["active-game"],
      });
      queryClient.invalidateQueries({
        queryKey: ["game-sessions", variables.mentorship_id],
      });
    },
  });
};

export const useEndGame = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["end-game"],
    mutationFn: async (data: EndGameParams) => {
      const response = await fetchApi<{ game_session: GameSession }>({
        url: `/v2/games/end`,
        method: "POST",
        data,
      });

      return response.data.game_session;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["active-game"],
      });
    },
  });
};

export const useCreateMentorGame = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["create-mentor-game"],
    mutationFn: async (data: CreateMentorGameParams) => {
      const response = await fetchApi<{ game_session: GameSession }>({
        url: `/v2/games/mentor`,
        method: "POST",
        data,
      });

      return response.data.game_session;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["active-game"],
      });
      queryClient.invalidateQueries({
        queryKey: ["game-sessions", variables.mentorship_id],
      });
    },
  });
};
